import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../CSS/CRUDfiles.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const TEACHER_STATUS_SEARCH_FIELDS = ['status', 'description'];
const TEACHER_STATUS_SORT_OPTIONS = [
  { key: 'status', label: 'Status', accessor: (s) => s.status?.toLowerCase() ?? '' },
];


const TeacherStatusComponent = () => {
  const [statuses, setStatuses] = useState([]);
  const [currentStatus, setCurrentStatus] = useState({ status: '', description: '' });
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState(null);
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  const {
    items: visibleStatuses,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(statuses, {
    searchFields: TEACHER_STATUS_SEARCH_FIELDS,
    sortOptions: TEACHER_STATUS_SORT_OPTIONS,
  });


  // Authentication check
  useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
  }, [authState, navigate]);

  // Fetch all teacher statuses
  useEffect(() => {
    fetchTeacherStatuses();
  }, []);

  const fetchTeacherStatuses = async () => {
    const response = await fetch(`${backendURL}/api/teacherstatuses`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await response.json();
    setStatuses(data);
  };

  const handleFormChange = (e) => {
    setCurrentStatus({ ...currentStatus, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset modal states
    setShowStatusModal(false);
    setShowDescriptionModal(false);

    // Check if status is empty
    if (!currentStatus.status || currentStatus.status.trim() === '') {
      setShowStatusModal(true);
      return;
    }

    // Check if description is empty
    if (!currentStatus.description || currentStatus.description.trim() === '') {
      setShowDescriptionModal(true);
      return;
    }

    if (currentStatus.teacher_status_id) {
      await updateTeacherStatus(currentStatus.teacher_status_id, currentStatus);
    } else {
      await createTeacherStatus(currentStatus);
    }
    setCurrentStatus({ status: '', description: '' });
    setShowForm(false);
    fetchTeacherStatuses();
  };


  const createTeacherStatus = async (statusData) => {
    await fetch(`${backendURL}/api/teacherstatuses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(statusData),
      credentials: 'include'
    });
  };

  const updateTeacherStatus = async (statusId, statusData) => {
    await fetch(`${backendURL}/api/teacherstatuses/${statusId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(statusData),
      credentials: 'include'
    });
  };

  const deleteTeacherStatus = async () => {
    if (statusToDelete) {
      await fetch(`${backendURL}/api/teacherstatuses/${statusToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setStatusToDelete(null);
      setShowModal(false);
      fetchTeacherStatuses();
    }
  };

  const editTeacherStatus = (status) => {
    setCurrentStatus(status);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentStatus({ status: '', description: '' });
    setShowForm(false);
  }

  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentStatus({ status: '', description: '' });
    }
    setShowForm(!showForm);
  };

  const statusForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group controlId="status">
          <Form.Label>Status</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter status"
            name="status"
            onChange={handleFormChange}
            value={currentStatus.status}
          />
        </Form.Group>
        <Form.Group controlId="description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            placeholder="Enter description"
            name="description"
            onChange={handleFormChange}
            value={currentStatus.description}
          />
        </Form.Group>
        <FormActions>
          <Button type="submit">Submit</Button>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </FormActions>
      </FormGrid>
    </Form>
  );

  return (
    <div>
      <CrudListShell
        addLabel="Add New Status"
        listLabel="Show Statuses List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="Teacher Statuses"
        form={statusForm}
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="status"
                nounPlural="statuses"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search statuses" placeholder="Status or description" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No teacher statuses have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleStatuses.map((status) => (
          <ExpandableRow
            key={status.teacher_status_id}
            main={{ title: status.status, subtitle: status.description || 'No description provided.' }}
            actions={(
              <>
                <Button size="sm" onClick={() => editTeacherStatus(status)} aria-label="Edit status">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setStatusToDelete(status.teacher_status_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete status"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
          />
        ))}
      </CrudListShell>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteTeacherStatus}
        bodyText="Are you sure you want to delete this status?"
      />
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Missing Status</Modal.Title>
      </Modal.Header>
      <Modal.Body>Please enter a status.</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showDescriptionModal} onHide={() => setShowDescriptionModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Missing Description</Modal.Title>
      </Modal.Header>
      <Modal.Body>Please enter a description.</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowDescriptionModal(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>


    </div>
  );
};

export default TeacherStatusComponent;
